import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { withTheme } from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { Theme as MuiTheme } from '@rjsf/mui';
import { RJSFSchema, UiSchema, RegistryWidgetsType } from '@rjsf/utils';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { RelationshipWidget } from './widgets/RelationshipWidget';

const Form = withTheme(MuiTheme);

// Custom widgets for RJSF
const customWidgets: RegistryWidgetsType = {
  relationship: RelationshipWidget,  // Widget for entity relationships
};

interface JSONSchemaFormProps {
  schema: RJSFSchema;
  formData: Record<string, any>;
  uiSchema?: UiSchema;
  onChange: (data: Record<string, any>) => void;
  defer?: boolean;
  loadingFallback?: React.ReactNode;
}

export const JSONSchemaForm: React.FC<JSONSchemaFormProps> = ({
  schema,
  formData,
  uiSchema,
  onChange,
  defer = true,
  loadingFallback = <div>Loading form...</div>,
}) => {
  const theme = useTheme();
  const backgroundContainer = useRef<HTMLDivElement | null>(null);
  const [isReady, setIsReady] = useState(!defer);

  // Step 1: defer ready flag until after initial render cycle
  useEffect(() => {
    if (defer && !isReady) {
      const timeout = setTimeout(() => {
        setIsReady(true);
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [defer, isReady]);

  // Form element (expensive) is only built when ready
  const visibleForm = (
    <ThemeProvider theme={theme}>
      <Form
        schema={schema}
        formData={formData}
        uiSchema={uiSchema || {}}
        validator={validator}
        widgets={customWidgets}  // ← Register custom widgets!
        onChange={({ formData }) => onChange(formData)}
        showErrorList={false}
        noHtml5Validate
      >
        <div style={{ display: 'none' }} />
      </Form>
    </ThemeProvider>
  );

  // When not ready: render offscreen version in portal (background init)
  if (!isReady && defer) {
    console.log('not ready and defer');
    return (
      <>
        <div ref={backgroundContainer} style={{ display: 'none' }} />
        {backgroundContainer.current &&
          createPortal(visibleForm, backgroundContainer.current)}
        {loadingFallback}
      </>
    );
  }

  console.log('isReady fully loaded path');

  // When ready: render in normal flow
  return visibleForm;
};